import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Flex,
  Text,
  Button,
  TextField,
} from '@radix-ui/themes';
import { Dialog } from '@/components/shared/dialog';
import Select from '@/components/shared/select';
import {
  useGetAvailableFields,
  useGetFieldSyncConfigurations,
  useCreateFieldSyncConfiguration,
  useUpdateFieldSyncConfiguration,
  QuackField,
  MappingType,
  QUACK_FIELD_LABELS,
  MAPPING_TYPE_LABELS,
  type FieldSyncConfiguration,
} from '@/v2/entities/field-sync';
import { useGetEnrichments } from './useGetEnrichments';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingConfig: FieldSyncConfiguration | null;
}

interface FormData {
  quackField: QuackField | '';
  mappingType: MappingType | '';
  targetFieldId: string;
  targetFieldName: string;
  enrichmentId: string;
}

export function FieldSyncDialog({ open, onOpenChange, editingConfig }: Props) {
  const { data: enrichments = [], isLoading: loadingEnrichments } =
    useGetEnrichments();
  const { refetch: refetchConfigurations } = useGetFieldSyncConfigurations();
  const createConfiguration = useCreateFieldSyncConfiguration();
  const updateConfiguration = useUpdateFieldSyncConfiguration();

  const [formData, setFormData] = useState<FormData>({
    quackField: '',
    mappingType: '',
    targetFieldId: '',
    targetFieldName: '',
    enrichmentId: '',
  });

  const {
    data: availableFields = [],
    isLoading: loadingFields,
    refetch: refetchAvailableFields,
  } = useGetAvailableFields(
    formData.mappingType === MappingType.CUSTOM_FIELD ? formData.mappingType : null
  );

  // Initialize form data when editing or opening dialog
  useEffect(() => {
    if (open) {
      if (editingConfig) {
        setFormData({
          quackField: editingConfig.quackField,
          mappingType: editingConfig.mappingType,
          targetFieldId: editingConfig.targetFieldId || '',
          targetFieldName: editingConfig.targetFieldName,
          enrichmentId: editingConfig.enrichmentId || '',
        });
      } else {
        setFormData({
          quackField: '',
          mappingType: '',
          targetFieldId: '',
          targetFieldName: '',
          enrichmentId: '',
        });
      }
    }
  }, [editingConfig, open]);

  const handleChange = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));

      // Auto-populate targetFieldName when selecting custom field
      if (field === 'targetFieldId' && formData.mappingType === MappingType.CUSTOM_FIELD) {
        const selectedField = availableFields.find((f) => f.id === value);
        if (selectedField) {
          setFormData((prev) => ({
            ...prev,
            targetFieldName: selectedField.name,
          }));
        }
      }

      // Clear target field data when mapping type changes
      if (field === 'mappingType') {
        setFormData((prev) => ({
          ...prev,
          targetFieldId: '',
          targetFieldName: '',
        }));
      }

      // Clear enrichment when quack field changes
      if (field === 'quackField' && value !== QuackField.ENRICHMENT) {
        setFormData((prev) => ({
          ...prev,
          enrichmentId: '',
        }));
      }
    },
    [availableFields, formData.mappingType]
  );

  const handleSubmit = useCallback(async () => {
    if (!formData.quackField || !formData.mappingType || !formData.targetFieldName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.quackField === QuackField.ENRICHMENT && !formData.enrichmentId) {
      toast.error('Please select an enrichment');
      return;
    }

    if (
      formData.mappingType === MappingType.CUSTOM_FIELD &&
      !formData.targetFieldId
    ) {
      toast.error('Please select a target custom field');
      return;
    }

    try {
      if (editingConfig) {
        await updateConfiguration.mutateAsync({
          id: editingConfig.id,
          data: {
            targetFieldId: formData.targetFieldId || undefined,
            targetFieldName: formData.targetFieldName,
          },
        });
        toast.success('Field mapping updated successfully');
      } else {
        await createConfiguration.mutateAsync({
          quackField: formData.quackField as QuackField,
          mappingType: formData.mappingType as MappingType,
          targetFieldId: formData.targetFieldId || undefined,
          targetFieldName: formData.targetFieldName,
          enrichmentId: formData.enrichmentId || undefined,
        });
        toast.success('Field mapping created successfully');
      }
      await refetchConfigurations();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save field mapping');
    }
  }, [formData, editingConfig, createConfiguration, updateConfiguration, onOpenChange, refetchConfigurations]);

  const isFormValid = useMemo(() => {
    if (!formData.quackField || !formData.mappingType || !formData.targetFieldName) {
      return false;
    }

    if (formData.quackField === QuackField.ENRICHMENT && !formData.enrichmentId) {
      return false;
    }

    if (
      formData.mappingType === MappingType.CUSTOM_FIELD &&
      !formData.targetFieldId
    ) {
      return false;
    }

    return true;
  }, [formData]);

  const quackFieldOptions = Object.values(QuackField).map((value) => ({
    value,
    label: QUACK_FIELD_LABELS[value],
  }));

  const mappingTypeOptions = [
    { value: MappingType.CUSTOM_FIELD, label: MAPPING_TYPE_LABELS[MappingType.CUSTOM_FIELD] },
    { value: MappingType.TAG, label: MAPPING_TYPE_LABELS[MappingType.TAG] },
  ];

  const isLoading = createConfiguration.isLoading || updateConfiguration.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Dialog.Title>
          {editingConfig ? 'Edit Field Mapping' : 'Add Field Mapping'}
        </Dialog.Title>

        <Flex direction="column" gap="4" p="4">
          {/* Quack Field Selection */}
          <Flex direction="column" gap="2">
            <Text as="label" size="2" weight="medium">
              Quack Field <Text color="red" highContrast>*</Text>
            </Text>
            <Select
              value={formData.quackField}
              onChange={(value) => handleChange('quackField', value)}
              options={quackFieldOptions}
              placeholder="Select Quack field"
              disabled={!!editingConfig}
            />
          </Flex>

          {/* Enrichment Selection (conditional) */}
          {formData.quackField === QuackField.ENRICHMENT && (
            <Flex direction="column" gap="2">
              <Text as="label" size="2" weight="medium">
                Enrichment <Text color="red" highContrast>*</Text>
              </Text>
              <Select
                value={formData.enrichmentId}
                onChange={(value) => handleChange('enrichmentId', value)}
                options={enrichments.map((enrichment) => ({
                  label: enrichment.name,
                  value: enrichment.id,
                }))}
                placeholder="Select enrichment"
                disabled={!!editingConfig || loadingEnrichments}
                loading={loadingEnrichments}
              />
            </Flex>
          )}

          {/* Mapping Type */}
          <Flex direction="column" gap="2">
            <Text as="label" size="2" weight="medium">
              Mapping Type <Text color="red" highContrast>*</Text>
            </Text>
            <Select
              value={formData.mappingType}
              onChange={(value) => handleChange('mappingType', value)}
              options={mappingTypeOptions}
              placeholder="Select mapping type"
            />
          </Flex>

          {/* Custom Field Selection */}
          {formData.mappingType === MappingType.CUSTOM_FIELD && (
            <Flex direction="column" gap="2">
              <Text as="label" size="2" weight="medium">
                Target Custom Field <Text color="red" highContrast>*</Text>
              </Text>
              {availableFields.length > 0 || loadingFields ? (
                <Select
                  value={formData.targetFieldId}
                  onChange={(value) => handleChange('targetFieldId', value)}
                  options={availableFields.map((field) => ({
                    label: field.type ? `${field.name} (${field.type})` : field.name,
                    value: field.id,
                  }))}
                  placeholder="Select custom field"
                  loading={loadingFields}
                  disabled={loadingFields}
                />
              ) : (
                <Text size="2" color="gray" highContrast>
                  No custom fields available in your ticketing system
                </Text>
              )}
            </Flex>
          )}

          {/* Tag Name Input */}
          {formData.mappingType === MappingType.TAG && (
            <Flex direction="column" gap="2">
              <Text as="label" size="2" weight="medium">
                Tag Prefix <Text color="red" highContrast>*</Text>
              </Text>
              <TextField.Root
                placeholder="e.g., topic, status, agent"
                value={formData.targetFieldName}
                onChange={(e) => handleChange('targetFieldName', e.target.value)}
                variant="surface"
              />
              <Text size="1" color="gray" highContrast>
                Tag will be formatted as: prefix:value (e.g., topic:billing)
              </Text>
            </Flex>
          )}
        </Flex>

        <Flex gap="3" justify="end" mt="6" pt="4" px="4" pb="4">
          <Button
            variant="soft"
            color="gray"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            highContrast
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            highContrast
          >
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
